import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { getGroceryUsageStatsLast24Hours } from "../services/groceryStore";
import "./Grocery.css";

const formatCompact = (value) =>
  new Intl.NumberFormat("en-PH", { notation: "compact" }).format(value || 0);

const formatFull = (value) =>
  new Intl.NumberFormat("en-PH", { maximumFractionDigits: 0 }).format(
    value || 0,
  );

function GroceryStatsPage() {
  const [state, setState] = useState({
    status: "loading",
    points: [],
    totals: { reads: 0, writes: 0, deletes: 0 },
    updatedAt: "",
    source: "",
    error: "",
  });

  useEffect(() => {
    let isMounted = true;

    const loadStats = async () => {
      try {
        setState((current) => ({ ...current, status: "loading", error: "" }));
        const payload = await getGroceryUsageStatsLast24Hours();

        if (!isMounted) {
          return;
        }

        setState({
          status: "ready",
          points: payload.points || [],
          totals: payload.totals || { reads: 0, writes: 0, deletes: 0 },
          updatedAt: payload.updatedAt || "",
          source: payload.source || "cloud-monitoring",
          error: "",
        });
      } catch (error) {
        if (!isMounted) {
          return;
        }

        setState((current) => ({
          ...current,
          status: "error",
          error: error.message || "Usage metrics are unavailable right now.",
        }));
      }
    };

    loadStats();

    return () => {
      isMounted = false;
    };
  }, []);

  const maxBucketValue = useMemo(() => {
    if (state.points.length === 0) {
      return 1;
    }

    return state.points.reduce((max, point) => {
      const total =
        (point.reads || 0) + (point.writes || 0) + (point.deletes || 0);
      return Math.max(max, total);
    }, 1);
  }, [state.points]);

  return (
    <main className="grocery-page">
      <section className="grocery-hero">
        <p className="grocery-kicker">Firestore Usage</p>
        <h1>Grocery Metrics Dashboard</h1>
        <p>App-tracked reads, writes, and deletes over the past 24 hours.</p>
        <div className="grocery-actions">
          <Link className="grocery-link-button" to="/grocery">
            Open Grocery Search
          </Link>
          <Link className="grocery-link-subtle" to="/grocery/add-items">
            Add Items
          </Link>
        </div>
      </section>

      <section className="grocery-layout grocery-layout-single">
        <article className="grocery-stats-shell">
          <div className="grocery-stats-head">
            <h2>Last 24 Hours</h2>
            <p>
              {state.updatedAt
                ? `Updated ${new Date(state.updatedAt).toLocaleString("en-PH")}`
                : "Waiting for latest data..."}
            </p>
          </div>

          {state.status === "loading" ? (
            <p className="grocery-message">Loading metrics...</p>
          ) : null}

          {state.status === "error" ? (
            <p className="grocery-message grocery-error">{state.error}</p>
          ) : null}

          {state.status === "ready" ? (
            <>
              <div className="grocery-stats-cards">
                <div className="grocery-stats-card">
                  <p>Reads</p>
                  <strong>{formatCompact(state.totals.reads)}</strong>
                  <small>{formatFull(state.totals.reads)}</small>
                </div>
                <div className="grocery-stats-card">
                  <p>Writes</p>
                  <strong>{formatCompact(state.totals.writes)}</strong>
                  <small>{formatFull(state.totals.writes)}</small>
                </div>
                <div className="grocery-stats-card">
                  <p>Deletes</p>
                  <strong>{formatCompact(state.totals.deletes)}</strong>
                  <small>{formatFull(state.totals.deletes)}</small>
                </div>
              </div>

              <div
                className="grocery-stats-chart"
                role="img"
                aria-label="Hourly usage chart"
              >
                {state.points.map((point) => {
                  const reads = point.reads || 0;
                  const writes = point.writes || 0;
                  const deletes = point.deletes || 0;
                  const sum = reads + writes + deletes;
                  const ratio = Math.max(sum / maxBucketValue, 0.03);
                  const label = point.label || "--";

                  return (
                    <article className="grocery-stats-row" key={point.ts}>
                      <span className="grocery-stats-time">{label}</span>
                      <div className="grocery-stats-bar-wrap">
                        <div
                          className="grocery-stats-bar"
                          style={{ width: `${Math.min(100, ratio * 100)}%` }}
                        >
                          <span
                            className="grocery-stats-segment grocery-stats-segment-reads"
                            style={{
                              width: `${sum ? (reads / sum) * 100 : 0}%`,
                            }}
                          />
                          <span
                            className="grocery-stats-segment grocery-stats-segment-writes"
                            style={{
                              width: `${sum ? (writes / sum) * 100 : 0}%`,
                            }}
                          />
                          <span
                            className="grocery-stats-segment grocery-stats-segment-deletes"
                            style={{
                              width: `${sum ? (deletes / sum) * 100 : 0}%`,
                            }}
                          />
                        </div>
                      </div>
                      <span className="grocery-stats-value">
                        {formatFull(sum)}
                      </span>
                    </article>
                  );
                })}
              </div>

              <p className="grocery-stats-source">Source: {state.source}</p>
            </>
          ) : null}
        </article>
      </section>
    </main>
  );
}

export default GroceryStatsPage;
